/**
 * PATH: apps/hr/src/pages/documents/document-detail/ExtractedFieldsSection.tsx
 * PURPOSE: Editable grid of OCR-extracted fields with confidence scores
 */

import { Edit, RefreshCw, AlertTriangle } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { getConfidenceColorClass } from './DetailBadges'
import type { ExtractedField } from '../../../types'

interface ExtractedFieldsSectionProps {
  fields: ExtractedField[]
  isEditing: boolean
  editedFields: Record<string, string>
  isSaving: boolean
  onToggleEdit: () => void
  onFieldEdit: (fieldName: string, value: string) => void
  onSave: () => void
  onCancel: () => void
}

export function ExtractedFieldsSection({
  fields, isEditing, editedFields, isSaving,
  onToggleEdit, onFieldEdit, onSave, onCancel,
}: ExtractedFieldsSectionProps) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Extracted Fields</h2>
        <button onClick={onToggleEdit} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <Edit className="w-4 h-4" />
          {isEditing ? 'Cancel' : 'Edit Fields'}
        </button>
      </div>

      {fields && fields.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field, i) => (
            <div key={i} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400">{field.name}</label>
                <span className={cn('text-xs font-medium', getConfidenceColorClass(field.confidence))}>
                  {Math.round(field.confidence)}% confidence
                </span>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  value={editedFields[field.name] !== undefined ? editedFields[field.name] : field.value}
                  onChange={(e) => onFieldEdit(field.name, e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              ) : (
                <p className={cn('font-medium text-slate-900 dark:text-white', field.confidence < 70 && 'text-amber-600 dark:text-amber-400')}>
                  {field.value || '-'}
                  {field.confidence < 70 && <AlertTriangle className="w-4 h-4 inline ml-2 text-amber-500" />}
                </p>
              )}
              {field.isEdited && field.originalValue && (
                <p className="text-xs text-amber-600 mt-1">Original: {field.originalValue}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No extracted fields available</p>
      )}

      {isEditing && (
        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            Cancel
          </button>
          <button onClick={onSave} disabled={isSaving} className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 flex items-center gap-2">
            {isSaving ? (<><RefreshCw className="w-4 h-4 animate-spin" />Saving...</>) : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  )
}
